import React, { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import "./../index.css";
import mainImg from "./../assets/main-nav2.png";
import projectIcon from "./../assets/project-icon.svg";
import skillsIcon from "./../assets/skills-icon.svg";
import { scrollToSection } from "./ScrollToSection";

const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  const navigate = useNavigate();
  const currentLocation = useLocation();

  // Handle scroll effect for navbar background
  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const handleSmartClick = (section) => {
    setIsOpen(false);
    if (currentLocation.pathname !== "/") {
      navigate(`/#${section}`);
      setTimeout(() => {
        scrollToSection(section);
      }, 100);
    } else {
      scrollToSection(section);
    }
  };

  const navLinks = [
    { name: "Home", icon: "bx-home" },
    { name: "AboutUs", label: "About", icon: "bx-user" },
    { name: "Projects", icon: "bx-code-alt", img: projectIcon },
    { name: "CaseStudies", label: "Case Studies", icon: "bx-layer" },
    { name: "Venture", label: "Agency", icon: "bx-briefcase" },
    { name: "Skills", icon: "bx-bulb", img: skillsIcon },
  ];

  return (
    <>
      <link
        rel="stylesheet"
        href="https://unpkg.com/boxicons@2.0.7/css/boxicons.min.css"
      />

      <nav
        className={`fixed top-0 w-full z-50 transition-all duration-300 border-b border-white/5 ${scrolled || isOpen
          ? "bg-[#030712]/70 backdrop-blur-xl supports-[backdrop-filter]:bg-[#030712]/70"
          : "bg-transparent backdrop-blur-sm"
          }`}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-20">

            {/* Logo */}
            <div className="flex-shrink-0 cursor-pointer group z-50 relative">
              <div onClick={() => handleSmartClick("Home")} className="flex items-center gap-2">
                <span data-egg-logo className="text-2xl font-black text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 to-blue-600 group-hover:to-cyan-300 transition-all duration-300">
                  RITESH
                </span>
              </div>
            </div>

            {/* Desktop Menu */}
            <div className="hidden md:block">
              <div className="ml-10 flex items-baseline space-x-8">
                {navLinks.map((link) => (
                  <button
                    key={link.name}
                    onClick={() => handleSmartClick(link.name)}
                    className="relative px-3 py-2 text-sm font-medium text-gray-300 hover:text-white transition-colors group"
                  >
                    {link.label || link.name}
                    <span className="absolute bottom-0 left-0 w-0 h-0.5 bg-gradient-to-r from-cyan-400 to-blue-500 transition-all duration-300 group-hover:w-full opacity-80" />
                  </button>
                ))}
              </div>
            </div>

            {/* Contact Button (Desktop) */}
            <div className="hidden md:block">
              <button
                onClick={() => handleSmartClick("Contact")}
                className="relative inline-flex h-10 overflow-hidden rounded-full p-[1px] focus:outline-none focus:ring-2 focus:ring-cyan-400 focus:ring-offset-2 focus:ring-offset-slate-900"
              >
                <span className="absolute inset-[-1000%] animate-[spin_2s_linear_infinite] bg-[conic-gradient(from_90deg_at_50%_50%,#E2CBFF_0%,#393BB2_50%,#E2CBFF_100%)]" />
                <span className="inline-flex h-full w-full cursor-pointer items-center justify-center rounded-full bg-slate-950 px-6 py-1 text-sm font-medium text-white backdrop-blur-3xl transition-colors hover:bg-slate-950/50">
                  Contact Me
                </span>
              </button>
            </div>

            {/* Mobile Hamburger */}
            <div className="-mr-2 flex md:hidden z-50 relative">
              <button
                onClick={() => setIsOpen(!isOpen)}
                className="inline-flex items-center justify-center p-2 rounded-md text-gray-400 hover:text-white hover:bg-white/10 focus:outline-none transition-all"
              >
                <div className="w-6 h-6 flex flex-col justify-center gap-1.5">
                  <span className={`block h-0.5 w-full bg-current transition-all duration-300 ${isOpen ? "rotate-45 translate-y-2" : ""}`} />
                  <span className={`block h-0.5 w-full bg-current transition-all duration-300 ${isOpen ? "opacity-0" : ""}`} />
                  <span className={`block h-0.5 w-full bg-current transition-all duration-300 ${isOpen ? "-rotate-45 -translate-y-2" : ""}`} />
                </div>
              </button>
            </div>
          </div>
        </div>

        {/* Mobile Sidebar (Drawer) */}
        <AnimatePresence>
          {isOpen && (
            <>
              {/* Backdrop Overlay */}
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="fixed inset-0 bg-black/80 backdrop-blur-sm z-40 md:hidden cursor-pointer"
                onClick={(e) => {
                  e.stopPropagation();
                  setIsOpen(false);
                }}
              />

              {/* Sidebar Content */}
              <motion.div
                initial={{ x: "100%" }}
                animate={{ x: 0 }}
                exit={{ x: "100%" }}
                transition={{ type: "spring", damping: 25, stiffness: 200 }}
                className="fixed right-0 top-0 h-screen w-[85%] max-w-[300px] bg-[#0b0c10] border-l border-white/10 shadow-2xl z-50 md:hidden flex flex-col"
                onClick={(e) => e.stopPropagation()} // Prevent clicks inside sidebar from closing it
              >
                {/* Drawer Header Background */}
                <div className="absolute top-0 left-0 w-full h-32 bg-gradient-to-b from-cyan-900/20 to-transparent pointer-events-none" />

                {/* Close Button */}
                <button
                  onClick={() => setIsOpen(false)}
                  className="absolute top-6 right-6 p-2 rounded-full text-white/50 hover:text-white hover:bg-white/10 transition-all z-50"
                >
                  <i className='bx bx-x text-3xl'></i>
                </button>

                <div className="flex flex-col h-full px-6 pt-24 pb-6 overflow-y-auto">

                  {/* Profile Section */}
                  <div className="flex flex-col items-center mb-10">
                    <div className="w-20 h-20 rounded-full p-[2px] bg-gradient-to-tr from-cyan-400 to-purple-500 mb-4 shadow-lg shadow-cyan-500/20">
                      <img
                        src={mainImg}
                        alt="Ritesh"
                        className="w-full h-full rounded-full object-cover border-2 border-[#0b0c10]"
                      />
                    </div>
                    <h2 className="text-xl font-bold text-white">Ritesh</h2>
                    <p className="text-sm text-gray-400">Full Stack Developer</p>
                  </div>

                  {/* Navigation Links */}
                  <div className="flex-1 space-y-4">
                    {navLinks.map((link, idx) => (
                      <motion.button
                        key={link.name}
                        initial={{ opacity: 0, x: 20 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: 0.1 + idx * 0.1 }}
                        onClick={() => handleSmartClick(link.name)}
                        className="w-full group flex items-center gap-4 p-3 rounded-xl hover:bg-white/5 transition-all text-left"
                      >
                        <div className="w-10 h-10 rounded-lg bg-white/5 flex items-center justify-center text-cyan-400 group-hover:bg-cyan-500/10 group-hover:scale-110 transition-all">
                          {link.img ? (
                            <img src={link.img} alt="" className="w-5 h-5 opacity-80" />
                          ) : (
                            <i className={`bx ${link.icon} text-xl`}></i>
                          )}
                        </div>
                        <span className="text-lg font-medium text-gray-300 group-hover:text-white">
                          {link.label || link.name}
                        </span>
                      </motion.button>
                    ))}
                  </div>

                  {/* Footer / CTA */}
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.5 }}
                    className="mt-8 pt-6 border-t border-white/10"
                  >
                    <button
                      onClick={() => handleSmartClick("Contact")}
                      className="w-full py-4 rounded-xl bg-gradient-to-r from-cyan-600 to-blue-600 text-white font-bold shadow-lg hover:shadow-cyan-500/25 active:scale-95 transition-all flex items-center justify-center gap-2"
                    >
                      <span>Let's Talk</span>
                      <i className='bx bx-send'></i>
                    </button>
                  </motion.div>
                </div>
              </motion.div>
            </>
          )}
        </AnimatePresence>
      </nav>
    </>
  );
};

export default Navbar;
